import { inject, Injectable, NgZone } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ShAbstractHttpService } from '../../core/http/services/http.abstract.service';
import { ShPaginationService } from '../../core/http/services/pagination.service';
import { cacheValueWhileSubscribed } from '../../helpers/typed-reactive-filters.helper';
import { MetabaseReport } from '../../metabase-reports/entities/report.entity';
import { MediaHavenPlugin, PluginObject } from '../../modules/entities/mediahaven-plugins.entity';
import { ShModulesService } from '../../modules/services/modules.service';
import { PaginatedResults } from '../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { mapToPaginatedResult } from '../../overviews/generic-overview/components/pagination/helpers/map-to-paginated-result.helper';
import { ShPreloadingStrategyService } from '../../core/application/preload/preload-strategy.service';
import { OrganisationId } from '../../core/organisations/entities/organisation.entity';

@Injectable()
export abstract class ShReportService {
    protected abstract readonly ENDPOINT: string;
    protected defaultCollection$: Observable<string | undefined>;
    protected defaultDashboard$: Observable<number | undefined>;

    // Injectors
    private readonly paginationService: ShPaginationService = inject(ShPaginationService);
    private readonly httpService: ShAbstractHttpService = inject(ShAbstractHttpService);
    private readonly moduleService: ShModulesService = inject(ShModulesService);
    private readonly preloadService: ShPreloadingStrategyService = inject(ShPreloadingStrategyService);
    private readonly http: HttpClient = inject(HttpClient);
    private readonly ngZone: NgZone = inject(NgZone);

    constructor() {
        this.initDefaults();
    }

    protected initDefaults(): void {
        const plugin$: Observable<PluginObject> = this.moduleService
            .getPlugin(MediaHavenPlugin.METABASE)
            .pipe(cacheValueWhileSubscribed());

        this.defaultCollection$ = plugin$.pipe(
            map(plugin => plugin.CustomProperties?.default_collection as string | undefined),
            shareReplay(1)
        );

        this.defaultDashboard$ = plugin$.pipe(
            map(plugin => plugin?.CustomProperties?.default_dashboard as number | undefined),
            shareReplay(1)
        );
    }

    private fetchReport(id: string): Observable<MetabaseReport> {
        return this.httpService.get<MetabaseReport>(`${this.ENDPOINT}/${id}`);
    }

    getAll(
        paginationInfo: PaginationInfo,
        organisationId?: string | OrganisationId
    ): Observable<PaginatedResults<MetabaseReport>> {
        return this.defaultCollection$.pipe(
            take(1),
            map(collection => {
                let params: HttpParams = new HttpParams();
                if (collection) {
                    params = params.append('collection', collection);
                }
                if (organisationId) {
                    params = params.append('organisationId', organisationId);
                }
                return params;
            }),
            switchMap(params =>
                this.paginationService.get<MetabaseReport>(
                    this.ENDPOINT,
                    params,
                    undefined,
                    paginationInfo.page,
                    paginationInfo.resultsPerPage
                )
            ),
            map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage))
        );
    }

    get(id: string): Observable<MetabaseReport> {
        return this.fetchReport(id);
    }

    getDefaultDashboardId(): Observable<number | undefined> {
        return this.defaultDashboard$;
    }

    public prefetchDashboard(): void {
        this.preloadService
            .preloadResource(
                () =>
                    this.getDefaultDashboardId().pipe(
                        filter(dashboardId => dashboardId !== undefined),
                        switchMap(dashboardId => this.get(dashboardId.toString()))
                    ),
                { delay: 0 }
            )
            .pipe(take(1))
            .subscribe(dashboard => {
                if (dashboard) {
                    this.prefetchIframe(dashboard.EmbedUrl);
                }
            });
    }

    public prefetchIframe(iframeUrl: string): void {
        this.ngZone.runOutsideAngular(() => {
            const iframe = document.createElement('iframe');
            iframe.src = iframeUrl;
            iframe.style.display = 'none';
            iframe.style.visibility = 'hidden';
            iframe.style.position = 'absolute';
            iframe.style.width = '1px';
            iframe.style.height = '1px';

            const timeoutMs = 15000; // Default 15 seconds
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, timeoutMs);

            document.body.appendChild(iframe);
        });
    }
}
